import { qfRestGet } from './qfClient';
import type {
  FznAlertRow,
  FznAlertsHistoryResponse,
  WiseguyAlert,
  WiseguyAlertsResponse,
} from '../types/wiseguy';

/**
 * FZN alerts have a hex `external_id`, not an integer PK. `alert.id` is a
 * display-only numeric handle synthesized from external_id via FNV-1a — used
 * for React keys / browser notification tags on surfaces that still expect a
 * number. Dedup identity is the string `external_id` itself (see `alertKey` in
 * wiseguy/alertStore.ts). Do not key Sets/Maps on the synthesized id: the hash
 * collides at real volumes and used to silently drop distinct alerts.
 */
function synthesizeId(externalId: string): number {
  // FNV-1a 32-bit — stable and fast. Kept positive so it never collides with a
  // portal PK's sign. Display-only; not a dedup key.
  let hash = 0x811c9dc5;
  for (let i = 0; i < externalId.length; i += 1) {
    hash ^= externalId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return Math.abs(hash | 0);
}

const BIAS_MAP: Record<string, WiseguyAlert['bias']> = {
  BULLISH: 'Bullish',
  BEARISH: 'Bearish',
  NEUTRAL: 'Neutral',
};

const CLASS_MAP: Record<string, WiseguyAlert['asset_class']> = {
  STOCK: 'Stock',
  ETF: 'ETF',
  INDEX: 'Index',
};

/**
 * QF's `side_descriptor` is an UPPER_SNAKE enum (e.g. `AT_ASK`), not the
 * 5-class human-readable label `aggressor_class` documents. Map the enum
 * onto that vocabulary; the raw QF value is kept separately on
 * `side_descriptor` (see below).
 */
const AGGRESSOR_CLASS_MAP: Record<string, string> = {
  ABOVE_ASK: 'Above Ask',
  AT_ASK: 'Ask Side',
  INSIDE_ASK: 'Ask Side',
  AT_MID: 'MidMkt',
  AT_BID: 'Bid Side',
  INSIDE_BID: 'Bid Side',
  BELOW_BID: 'Below Bid',
  // Legacy / alternate labels seen on older payloads
  MID: 'MidMkt',
  MIDMKT: 'MidMkt',
  UNKNOWN: '',
};

function toAggressorClass(sideDescriptor: string | null | undefined): string {
  if (!sideDescriptor) return '';
  return AGGRESSOR_CLASS_MAP[sideDescriptor.toUpperCase()] ?? '';
}

/**
 * REST ships `flag`; live often omits it. Derive from `alert_type` patterns
 * (field map §5): MULTI-SWEEP→sweep, *-TRADE→trade, *-SPLIT→split.
 */
export function flagFromAlertType(alertType: string | null | undefined): string | null {
  if (!alertType) return null;
  const t = alertType.toUpperCase();
  if (t.includes('SWEEP')) return 'sweep';
  if (t.includes('SPLIT')) return 'split';
  if (t.includes('TRADE')) return 'trade';
  return null;
}

function resolveFlag(
  flag: string | null | undefined,
  alertType: string | null | undefined
): string | null {
  if (flag) {
    const normalized = flag.toLowerCase();
    if (normalized === 'sweep' || normalized === 'trade' || normalized === 'split') {
      return normalized;
    }
  }
  return flagFromAlertType(alertType);
}

function formatBet(premium: number): string {
  if (premium >= 1_000_000) return `$${(premium / 1_000_000).toFixed(1)}M`;
  if (premium >= 1_000) return `$${(premium / 1_000).toFixed(1)}K`;
  return `$${premium}`;
}

function daysToExpiry(expiry: string): number {
  const ms = new Date(`${expiry}T00:00:00Z`).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

/**
 * Normalize a QuantForge AlertRow into the WiseguyAlert shape the widget
 * already consumes, so the ~2700 lines downstream of the data seams are
 * unaffected by the source change.
 */
export function adaptFznAlert(row: FznAlertRow): WiseguyAlert {
  const rawCondition = row.condition ?? '';
  // `opening` and `condition` are independent QF signals — `condition`
  // routinely already contains the comma-delimited "OPENING" token
  // (e.g. 'AUCTION,OPENING') even when `opening` is false. Only synthesize
  // the token when `opening` is true AND it isn't already present, using
  // QF's own comma-delimited format rather than a space join.
  const condition =
    row.opening && !rawCondition.split(',').includes('OPENING')
      ? [rawCondition, 'OPENING'].filter(Boolean).join(',')
      : rawCondition;

  return {
    id: synthesizeId(row.external_id),
    source: 'qf',
    external_id: row.external_id,
    symbol: row.symbol,
    underlying_symbol: row.underlying_symbol ?? row.symbol,
    bias: BIAS_MAP[(row.bias ?? '').toUpperCase()] ?? 'Neutral',
    contract_type: row.contract_type.toUpperCase().startsWith('P') ? 'Put' : 'Call',
    strike: String(row.strike),
    expiry: row.expiry,
    bet: row.premium,
    bet_display: formatBet(row.premium),
    reference_price: String(row.reference_price ?? ''),
    multiplier: String(row.multiplier ?? ''),
    alert_type: row.alert_type,
    asset_class: CLASS_MAP[(row.asset_class ?? '').toUpperCase()] ?? 'Stock',
    events: row.events ?? null,
    top_bet: row.top_bet,
    top_sweep: row.top_sweep,
    received_on: row.ts,
    time_to_expiry_days: daysToExpiry(row.expiry),
    formatted_text: null,
    display_text: `${row.symbol} ${row.strike} ${row.expiry}`,
    condition,
    comment: row.comment ?? null,
    was_posted: row.was_posted,
    n_legs: row.n_legs ?? null,
    n_venues: row.n_venues ?? null,
    span_ms: null,
    aggressor_class: toAggressorClass(row.side_descriptor),
    side_descriptor: row.side_descriptor ?? null,
    nbbo_bid: row.nbbo_bid != null ? String(row.nbbo_bid) : null,
    nbbo_ask: row.nbbo_ask != null ? String(row.nbbo_ask) : null,
    iv: row.iv ?? null,
    delta: row.delta ?? null,
    gamma: row.gamma ?? null,
    theta: row.theta ?? null,
    vega: row.vega ?? null,
    rho: row.rho ?? null,
    iv_chg: row.iv_chg ?? null,
    iv_chg_pct: row.iv_chg_pct ?? null,
    qty: row.qty ?? null,
    last_price: row.last_price != null ? String(row.last_price) : null,
    venue_tag: row.venue ?? null,
    flag: resolveFlag(row.flag, row.alert_type),
    open_interest: row.open_interest ?? null,
    volume: row.volume ?? null,
  };
}

/**
 * Normalize a live Centrifugo `tradealert:*` publication into the REST
 * `FznAlertRow` shape.
 *
 * As of QF 2026-08, `tradealert:wsj` membership matches REST Steamroom history
 * (product floor + curator promotion). `tradealert:stream` remains the full
 * firehose for QF's own board — Steamroom must not subscribe to it.
 *
 * The live wire payload is NOT always the REST AlertRow: QF's Go shapers emit
 * several fields under different names (`mult`, `class`, `cond`, `spot`,
 * `venue_tag`). Curator `alert.update` republishes a full row with overlay
 * fields set; plain `alert.new` may omit them — default overlay to false.
 * Casting the publication straight to FznAlertRow silently blanks renamed
 * fields, so map the names explicitly.
 *
 * Verified against `services/uw-tradealert/qf_shaper.go` (and the agg / v4 /
 * strategy shapers, which use the same key names).
 */
export function normalizeFznLivePayload(raw: Record<string, unknown>): FznAlertRow {
  const pick = <T>(...keys: string[]): T | undefined => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null) return v as T;
    }
    return undefined;
  };

  const alertType = String(pick<string>('alert_type', 'type') ?? '');
  const flagRaw = pick<string>('flag');
  const underlying = pick<string>('underlying_symbol');
  return {
    ...(raw as unknown as FznAlertRow),
    external_id: String(raw.external_id ?? ''),
    ts: String(raw.ts ?? ''),
    symbol: String(raw.symbol ?? ''),
    underlying_symbol: underlying != null ? String(underlying) : null,
    contract_type: String(raw.contract_type ?? ''),
    strike: Number(raw.strike ?? 0),
    expiry: String(raw.expiry ?? ''),
    premium: Number(raw.premium ?? 0),
    alert_type: alertType,
    // Renamed on the wire (live key → REST key).
    multiplier: pick<number>('multiplier', 'mult'),
    asset_class: pick<string>('asset_class', 'class'),
    condition: pick<string>('condition', 'cond') ?? null,
    reference_price: pick<number>('reference_price', 'spot'),
    venue: pick<string>('venue', 'venue_tag') ?? null,
    open_interest: pick<number>('open_interest', 'oi_at_alert') ?? null,
    // Session vol: REST `volume`; live may also send `session_vol`.
    volume: pick<number>('volume', 'session_vol') ?? null,
    // Live often omits flag — derive from alert_type (field map §5).
    flag: resolveFlag(flagRaw, alertType),
    opening: Boolean(raw.opening ?? false),
    // Overlay: present on alert.update republish; default false on alert.new.
    top_bet: Boolean(raw.top_bet ?? false),
    top_sweep: Boolean(raw.top_sweep ?? false),
    was_posted: Boolean(raw.was_posted ?? false),
    promotes_floor: Boolean(raw.promotes_floor ?? false),
    floor_state: typeof raw.floor_state === 'string' ? raw.floor_state : null,
    comment: (raw.comment as string | null | undefined) ?? null,
  };
}

/**
 * Curator de-promote on `tradealert:wsj`.
 *
 * QF still publishes the update onto this channel when a promoted row drops
 * below the product floor (`#944` mirrors de-promotes). Overlay all-false
 * (`promotes_floor` / `top_bet` / `top_sweep` / `was_posted`) plus
 * `floor_state=below` (or omitted on the live overlay) means the row is no
 * longer a Steamroom member. A floor-native edit keeps a non-below
 * `floor_state` and must stay on the tape.
 */
export function isFznUnpromote(row: FznAlertRow): boolean {
  if (row.promotes_floor || row.top_bet || row.top_sweep || row.was_posted) {
    return false;
  }
  const floor = (row.floor_state ?? '').trim().toLowerCase();
  return floor === 'below' || floor === '';
}

/** Server-classified earnings window — do not recompute client-side. */
export type SteamroomEarningsPhase = 'pre' | 'day_of' | 'post';

/** Earnings print timing for tooltips only. */
export type SteamroomEarningsTime = 'bmo' | 'amc' | 'dmh';

/**
 * Floored per-ticker meta for Steamroom member chrome.
 * Trust earnings_phase + week52; paint only — never re-window dates.
 */
export interface SteamroomTickerMeta {
  alert_count: number;
  next_earnings: string | null;
  last_earnings: string | null;
  earnings_phase: SteamroomEarningsPhase | null;
  earnings_time: SteamroomEarningsTime | null;
  week52: 'high' | 'low' | null;
}

export interface SteamroomTickersMetaResponse {
  tickers: Record<string, SteamroomTickerMeta>;
}

/** Normalize wire meta (defensive — unknown enums → null). */
export function normalizeSteamroomTickerMeta(
  raw: Partial<SteamroomTickerMeta> & Record<string, unknown>
): SteamroomTickerMeta {
  const phase = raw.earnings_phase;
  const et = raw.earnings_time;
  const w52 = raw.week52;
  return {
    alert_count: typeof raw.alert_count === 'number' ? raw.alert_count : 0,
    next_earnings:
      typeof raw.next_earnings === 'string' ? raw.next_earnings : null,
    last_earnings:
      typeof raw.last_earnings === 'string' ? raw.last_earnings : null,
    earnings_phase:
      phase === 'pre' || phase === 'day_of' || phase === 'post' ? phase : null,
    earnings_time: et === 'bmo' || et === 'amc' || et === 'dmh' ? et : null,
    week52: w52 === 'high' || w52 === 'low' ? w52 : null,
  };
}

export interface FznAlertParams {
  /**
   * Single ET calendar day, YYYY-MM-DD.
   * Used only when `from`/`to` are not supplied. Never send alongside a window.
   */
  date?: string;
  /**
   * Window start (inclusive), YYYY-MM-DD ET. Must be paired with `to` —
   * a lone `from` 422s on the Steamroom route.
   */
  from?: string;
  /**
   * Window end (inclusive), YYYY-MM-DD ET. Must be paired with `from`.
   */
  to?: string;
  limit?: number;
  /**
   * Opaque keyset token from a prior response's `next_cursor`.
   *
   * QF names the request param `cursor` (verified against
   * `web/app/routes/alerts.py`).
   */
  cursor?: string;
}

/**
 * WiseguyAlertsResponse plus QF's `truncated` flag and cursor. QF caps a
 * response at 5000 rows server-side; surfacing `truncated` stops a busy window
 * from silently reading as complete. `next_cursor` is passed through so
 * cursor paging within the window can advance.
 */
export interface FznAlertsResponse extends WiseguyAlertsResponse {
  /**
   * ET session QF served, `YYYY-MM-DD`. Empty when no session resolved.
   * Echo of the wire `session_date` — never invented from the clock.
   */
  session_date: string;
  truncated: boolean;
  next_cursor?: string | null;
}

/**
 * Build query params for Steamroom alert history.
 *
 * When `from` and `to` are both present, send the window and omit `date`.
 * When only `date` is set, send that day. When none of the three are set,
 * send no date at all so QF resolves the last session this scope has
 * `source=qf` rows (grain A). Never invent today from the clock. Never send
 * both modes.
 */
function buildAlertQuery(params: FznAlertParams): Record<string, string> {
  const query: Record<string, string> = {};
  if (params.from != null && params.to != null) {
    query.from = params.from;
    query.to = params.to;
  } else if (params.date) {
    query.date = params.date;
  }
  if (params.limit) query.limit = String(params.limit);
  if (params.cursor) query.cursor = params.cursor;
  return query;
}

function mapHistoryResponse(res: FznAlertsHistoryResponse): FznAlertsResponse {
  const data = res.alerts.map(adaptFznAlert);
  return {
    success: true,
    data,
    session_date: res.session_date ?? '',
    truncated: res.truncated,
    next_cursor: res.next_cursor,
    pagination: {
      page: 1,
      page_size: data.length,
      total_count: data.length,
      total_pages: 1,
    },
  };
}

export const fznService = {
  /**
   * Fetch FZN alerts from the Steamroom-mounted history endpoint.
   *
   * Pass `date` to pin a day (Wiseguy session mode sends ET today).
   * Omit `date` only when the caller wants QF to pick the last floored day.
   * `from`/`to` for multi-day (ticker search). Product floor is always
   * applied on this route — not a query param.
   */
  async getAlerts(params: FznAlertParams): Promise<FznAlertsResponse> {
    const query = buildAlertQuery(params);
    const res = await qfRestGet<FznAlertsHistoryResponse>(
      '/v1/steamroom/alerts/history',
      query
    );
    return mapHistoryResponse(res);
  },

  /**
   * Fetch FZN alerts for one underlying via the ticker-scoped Steamroom route.
   *
   * The path symbol wins — do not send a `tickers` query param (it is ignored).
   * Same response shape, floor, and param rules as `getAlerts`.
   */
  async getTickerAlerts(
    symbol: string,
    params: FznAlertParams
  ): Promise<FznAlertsResponse> {
    const query = buildAlertQuery(params);
    const path = `/v1/steamroom/tickers/${encodeURIComponent(symbol)}/alerts`;
    const res = await qfRestGet<FznAlertsHistoryResponse>(path, query);
    return mapHistoryResponse(res);
  },

  /**
   * Floored ticker meta for member UI (earnings phase, 52W, optional count).
   *
   * Prefer this over unfloored `/v1/tickers/meta` for Steamroom chrome.
   * Max 500 symbols per request (server cap).
   */
  async getTickersMeta(
    date: string,
    symbols?: string[]
  ): Promise<SteamroomTickersMetaResponse> {
    const query: Record<string, string> = { date };
    if (symbols && symbols.length > 0) {
      const capped = symbols
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)
        .slice(0, 500);
      if (capped.length > 0) query.symbols = capped.join(',');
    }
    const res = await qfRestGet<SteamroomTickersMetaResponse>(
      '/v1/steamroom/tickers/meta',
      query
    );
    const tickers: Record<string, SteamroomTickerMeta> = {};
    for (const [sym, raw] of Object.entries(res.tickers ?? {})) {
      tickers[sym.toUpperCase()] = normalizeSteamroomTickerMeta(
        raw as Partial<SteamroomTickerMeta> & Record<string, unknown>
      );
    }
    return { tickers };
  },
};
